import {
  ASSOCIATION_ENTITY_TO_MN_ENTITY_NAME,
  AssociationEntity,
  AssociationResponse,
  AssociationType,
  EntitySyncInfoResponse,
  ExternalSystem,
} from '@monetize/types/app';
import { arrayToObject } from '@monetize/utils/core';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { apiGet } from './axios';

export const associationsQueryKeys = {
  base: ['associations'] as const,
  getById: (entity: AssociationEntity, recordId: string) =>
    [...associationsQueryKeys.base, entity, recordId] as const,
  syncInfo: (entity: AssociationEntity, recordId: string) =>
    [...associationsQueryKeys.base, 'syncInfo', entity, recordId] as const,
};

// GET("/connector/accounts/{${PathVariable.MnAccountId}}/associations")
// GET("/connector/contacts/{${PathVariable.MnContactId}}/associations")
// GET("/connector/opportunities/{${PathVariable.MnOpportunityId}}/associations")

export function useGetAssociationsById<SelectData = AssociationResponse[]>(
  {
    entity,
    recordId,
  }: {
    entity: AssociationEntity;
    recordId: string;
  },
  options: Partial<
    UseQueryOptions<AssociationResponse[], unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...associationsQueryKeys.getById(entity, recordId)],
    queryFn: () =>
      apiGet<AssociationResponse[]>(
        `/connector/${entity}/${recordId}/associations`,
      ).then((res) =>
        res.data.filter((item) => item.status === AssociationType.ASSOCIATED),
      ),
    ...options,
  });
}

// GET("/connector/entities/{${PathVariable.MnEntityName}}/{${PathVariable.MnEntityId}}/syncInfo")

/**
 * Associations *and* recent sync logs for a record, in one request.
 *
 * Unlike {@link useGetAssociationsById}, whose route the connector only wires up
 * for accounts, contacts and opportunities, this endpoint is generic over every
 * `MnEntityName` — so it also works for invoices, payments, credit notes,
 * refunds and products.
 */
export function useGetEntitySyncInfo<SelectData = EntitySyncInfoResponse>(
  {
    entity,
    recordId,
  }: {
    entity: AssociationEntity;
    recordId: string;
  },
  options: Partial<
    UseQueryOptions<EntitySyncInfoResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...associationsQueryKeys.syncInfo(entity, recordId)],
    queryFn: () =>
      apiGet<EntitySyncInfoResponse>(
        `/connector/entities/${ASSOCIATION_ENTITY_TO_MN_ENTITY_NAME[entity]}/${recordId}/syncInfo`,
      ).then(({ data }) => ({
        // Archived/disassociated records are never surfaced in the UI.
        associations: data.associations.filter(
          ({ status }) => status === AssociationType.ASSOCIATED,
        ),
        syncLogs: data.syncLogs ?? [],
      })),
    ...options,
  });
}

export function useGetAssociationsByExternalSystem(
  { entity, recordId }: { entity: AssociationEntity; recordId: string },
  options: Partial<UseQueryOptions<AssociationResponse[], unknown>> = {},
) {
  return useGetAssociationsById<Record<ExternalSystem, AssociationResponse>>(
    { entity, recordId },
    {
      ...options,
      select: (data) => arrayToObject(data, 'externalSystem'),
    },
  );
}
